<script setup lang="ts">
interface MenuList {
	route: string;
	name: string;
}

const menuList: MenuList[] = [
	{
		route: ROUTE.VISIT_JAPAN.NAME,
		name: 'Visiting Japan',
	},
	{
		route: ROUTE.EVENT_CREATE.NAME,
		name: 'Create event',
	},
	{
		route: ROUTE.PROFILE_ATTENDING.NAME,
		name: 'My tickets',
	},
	{
		route: ROUTE.PROFILE_HOSTING.NAME,
		name: 'My profile and groups',
	},
	{
		route: ROUTE.SETTINGS.NAME,
		name: 'Settings',
	},
];

defineProps({
	isTransparentBackground: {
		type: Boolean,
		default: false,
	},
});

defineEmits<{ (e: 'onSignOut'): void }>();

const { isTablet } = useWindowResize();
const profileStore = useProfileStore();

const isOpen = ref(false);

const buttonSize = computed(() => {
	return isTablet.value ? BtnSize[0] : BtnSize[2];
});

function handleClose() {
	isOpen.value = false;
}

onMounted(async () => {
	if (!profileStore.profileId) {
		await profileStore.myAccountInfo();
	}
});

watch(
	() => profileStore.profileData,
	() => {
		// Check if user has a primary group or not,
		// If not, proceed to initialize a first Peasy group
		if (profileStore.profile && !profileStore.firstGroup?.id) {
			profileStore.initializeProfileGroup(profileStore.profile);
		}
	},
);
</script>

<template>
	<div class="user-nav">
		<!-- Navigation menu in desktop screen -->
		<template v-if="!isTablet">
			<RouterLink :to="ROUTE.VISIT_JAPAN.PATH">
				<BaseButton
					variant="subtle"
					class="nav-item"
					:color="isTransparentBackground ? BtnColor[3] : BtnColor[1]"
					:size="buttonSize"
				>
					Visiting Japan
				</BaseButton>
			</RouterLink>
			<RouterLink :to="ROUTE.EVENT_CREATE.PATH">
				<BaseButton
					variant="subtle"
					class="nav-item"
					:color="isTransparentBackground ? BtnColor[3] : BtnColor[1]"
					:size="buttonSize"
				>
					Create event
				</BaseButton>
			</RouterLink>
			<RouterLink
				:to="{
					name: ROUTE.PROFILE_ATTENDING.NAME,
				}"
			>
				<BaseButton
					variant="subtle"
					class="nav-item"
					:color="isTransparentBackground ? BtnColor[3] : BtnColor[1]"
					:size="buttonSize"
				>
					My tickets
				</BaseButton>
			</RouterLink>
			<div
				id="menu-activator"
				class="cursor-pointer"
			>
				<AvatarImage :image-url="profileStore.profile?.avatarUrl" />
			</div>
			<VMenu
				v-model="isOpen"
				activator="#menu-activator"
				location="bottom end"
				width="200"
			>
				<VList>
					<VListItem
						:ripple="false"
						:to="{
							name: ROUTE.PROFILE_HOSTING.NAME,
						}"
					>
						<VListItemAction>My profile and groups</VListItemAction>
					</VListItem>
					<VListItem
						:ripple="false"
						:to="{
							name: ROUTE.SETTINGS.NAME,
						}"
					>
						<VListItemAction>Settings</VListItemAction>
					</VListItem>
					<VListItem
						:ripple="false"
						@click="$emit('onSignOut')"
					>
						<VListItemAction>Sign out</VListItemAction>
					</VListItem>
				</VList>
			</VMenu>
		</template>
		<!-- End Navigation menu in desktop screen -->

		<!-- Navigation menu in mobile screen -->
		<template v-else>
			<AvatarImage
				:image-url="profileStore.profile?.avatarUrl"
				@click="isOpen = true"
			/>
			<MenuSideDrawer
				:is-open="isOpen"
				@on-close="handleClose"
			>
				<VList class="menu-list">
					<VListItem
						v-for="(menu, index) in menuList"
						:key="index"
						:ripple="false"
						:to="{
							name: menu.route,
						}"
						@click="handleClose"
					>
						<VListItemAction>{{ menu.name }}</VListItemAction>
					</VListItem>
					<VListItem
						tag="a"
						:ripple="false"
						@click="
							() => {
								handleClose;
								$emit('onSignOut');
							}
						"
					>
						<VListItemAction>Sign out</VListItemAction>
					</VListItem>
				</VList>
			</MenuSideDrawer>
		</template>
		<!-- End Navigation menu in mobile screen -->
	</div>
</template>

<style scoped lang="scss">
.user-nav {
	display: flex;
	align-items: center;
	gap: spacings-get(3);

	.nav-item {
		padding: rem(10) spacings-get(3);
	}
}
</style>
